import { useState, useCallback, useMemo, useEffect } from 'react';

/**
 * useUnifiedDocuments - Centralized document management hook for Driver/Vehicle entities
 *
 * Provides unified document state management with standardized referenceId format.
 * Automatically loads documents when referenceId becomes available.
 *
 * @param {Object} options
 * @param {'DRIVER'|'VEHICLE'} options.scope - Entity scope
 * @param {number|string|null} options.entityId - Entity ID (driver.id or vehicle.id)
 * @param {(referenceId: string) => Promise<{ok: boolean, data: any[]}>} options.fetcher - API fetch function
 * @returns {Object} Document state and actions
 */
function useUnifiedDocuments({ scope, entityId, fetcher }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);

  // Compute referenceId based on scope and entityId
  // Format: DRV_000123 or VHC_000456 (6-digit padded)
  const referenceId = useMemo(() => {
    if (!entityId) return null;
    const prefix = scope === 'DRIVER' ? 'DRV' : 'VHC';
    return `${prefix}_${String(entityId).padStart(6, '0')}`;
  }, [scope, entityId]);

  // Reload documents from API
  const reload = useCallback(async () => {
    if (!referenceId || !fetcher) return;

    setLoading(true);
    try {
      const result = await fetcher(referenceId);
      if (result.ok) {
        setDocuments(result.data || []);
      }
    } catch (err) {
      console.warn(`[useUnifiedDocuments] Failed to load documents for ${referenceId}:`, err);
    } finally {
      setLoading(false);
    }
  }, [referenceId, fetcher]);

  // Auto-load documents when referenceId changes
  useEffect(() => {
    if (referenceId && fetcher) {
      reload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceId]);

  return {
    referenceId,
    documents,
    loading,
    reload,
    setDocuments
  };
}

export default useUnifiedDocuments;
